import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heading } from '../../components/Heading';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { Select } from '../../components/Select';
import { Text } from '../../components/Text';
import { Badge } from '../../components/Badge';
import { Dialog, DialogActions, DialogBody, DialogDescription, DialogTitle } from '../../components/Dialog';
import { FiltersDialog, FilterPills, FilterPill } from '../../components/FiltersDialog';
import { ListToolbarRow, ListToolbarSearch, FiltersButton } from '../../components/ListToolbar';
import { useNotifications } from '../../hooks/useNotifications';
import type { Notification } from '../../services/notificationService';
import Loading from '../../components/Loading';
import Pagination from '../../components/Pagination';
import { cn } from '../../lib/utils';
import { BellIcon as BellOutlineIcon } from '@heroicons/react/24/outline';
import { BellIcon, CheckIcon, TrashIcon } from '@heroicons/react/20/solid';
import {
    NOTIFICATION_CATEGORIES,
    findNotificationCategory,
    findNotificationCategoryByValue,
    formatNotificationRelativeTime,
    groupNotificationsByTimeline,
} from '../../utils/notificationDisplay';

function EmptyState({ message }: { message: string }) {
    return (
        <div className="flex flex-col items-center justify-center py-16 px-4 rounded-lg bg-secondary/40">
            <BellOutlineIcon className="w-12 h-12 text-muted-foreground mb-3" aria-hidden />
            <p className="text-foreground font-medium">No notifications</p>
            <p className="text-sm text-muted-foreground mt-1">{message}</p>
        </div>
    );
}

const FETCH_LIMIT = 200;
const PAGE_SIZE = 10;

const NotificationsPage: React.FC = () => {
    const navigate = useNavigate();
    const [selectedCategory, setSelectedCategory] = useState('');
    const [search, setSearch] = useState('');
    const [unreadOnly, setUnreadOnly] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null);
    const [clearDialogOpen, setClearDialogOpen] = useState(false);
    const [clearing, setClearing] = useState(false);
    const [isFilterOpen, setIsFilterOpen] = useState(false);

    const { notifications, unreadCount, loading, markAllRead, markRead, clearAll } = useNotifications({
        limit: FETCH_LIMIT,
    });

    useEffect(() => {
        document.title = 'Notifications';
    }, []);

    const categoryCounts = useMemo(() => {
        const counts: Record<string, number> = {};
        for (const n of notifications) {
            const cat = findNotificationCategory(n.type);
            if (!cat) continue;
            counts[cat.value] = (counts[cat.value] || 0) + 1;
        }
        return counts;
    }, [notifications]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return notifications.filter((n) => {
            if (selectedCategory && findNotificationCategory(n.type)?.value !== selectedCategory) return false;
            if (unreadOnly && n.read_at) return false;
            if (q && !`${n.title} ${n.message ?? ''}`.toLowerCase().includes(q)) return false;
            return true;
        });
    }, [notifications, selectedCategory, unreadOnly, search]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const pageItems = useMemo(
        () => filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
        [filtered, currentPage]
    );
    const groups = useMemo(() => groupNotificationsByTimeline(pageItems), [pageItems]);
    const activeCategory = selectedCategory ? findNotificationCategoryByValue(selectedCategory) : null;

    useEffect(() => {
        setCurrentPage(1);
    }, [selectedCategory, unreadOnly, search]);

    useEffect(() => {
        if (currentPage > totalPages) setCurrentPage(totalPages);
    }, [currentPage, totalPages]);

    const resetFilters = () => {
        setSelectedCategory('');
        setUnreadOnly(false);
    };

    const activeFilterCount = (selectedCategory ? 1 : 0) + (unreadOnly ? 1 : 0);

    const filterPills: FilterPill[] = [
        ...(activeCategory ? [{ key: 'category', label: `Category: ${activeCategory.label}`, onClear: () => setSelectedCategory('') }] : []),
        ...(unreadOnly ? [{ key: 'unread', label: 'Unread only', onClear: () => setUnreadOnly(false) }] : []),
    ];

    const handleOpen = (n: Notification) => {
        if (!n.read_at) markRead(n.id);
        setSelectedNotification(n);
    };

    const handleClearAll = async () => {
        setClearing(true);
        try {
            await clearAll();
            setClearDialogOpen(false);
            setSelectedCategory('');
            setCurrentPage(1);
        } finally {
            setClearing(false);
        }
    };

    return (
        <>
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <Heading>Notifications</Heading>
                    <p className="mt-1 text-sm text-muted-foreground">
                        View and manage your notifications
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                    {unreadCount > 0 && (
                        <Button outline onClick={() => markAllRead()}>
                            <CheckIcon />
                            Mark all as read
                        </Button>
                    )}
                    {notifications.length > 0 && (
                        <Button outline onClick={() => setClearDialogOpen(true)}>
                            <TrashIcon />
                            Clear all
                        </Button>
                    )}
                </div>
            </div>

            <Card className="mt-6 p-5">
                <ListToolbarRow search={<ListToolbarSearch value={search} onChange={setSearch} placeholder="Search notifications…" />}>
                    <FiltersButton count={activeFilterCount} onClick={() => setIsFilterOpen(true)} />
                </ListToolbarRow>
                <FilterPills pills={filterPills} onClearAll={resetFilters} />

                <div>
                    {loading ? (
                        <div className="flex justify-center py-12">
                            <Loading />
                        </div>
                    ) : activeCategory?.placeholder ? (
                        <EmptyState message={`No notifications in ${activeCategory.label} yet.`} />
                    ) : filtered.length === 0 ? (
                        <EmptyState message={search || unreadOnly ? 'No notifications match your filters.' : "You're all caught up."} />
                    ) : (
                        <div className="space-y-6">
                            {groups.map((group) => (
                                <div key={group.label}>
                                    <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                        {group.label}
                                    </div>
                                    <ul className="space-y-2">
                                        {group.items.map((n) => {
                                            const cat = findNotificationCategory(n.type);
                                            const Icon = cat?.icon ?? BellIcon;
                                            return (
                                                <li key={n.id}>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleOpen(n)}
                                                        className="flex w-full items-start gap-3 rounded-lg p-3 text-left transition-colors hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-ring"
                                                    >
                                                        <span className={cn('flex size-9 shrink-0 items-center justify-center rounded-full', cat?.iconClass ?? 'bg-zinc-500/15 text-zinc-500')}>
                                                            <Icon className="size-4" />
                                                        </span>
                                                        <span className="min-w-0 flex-1">
                                                            <span className="flex items-start justify-between gap-2">
                                                                <span className="flex min-w-0 items-center gap-1.5">
                                                                    {!n.read_at && <span className="size-1.5 shrink-0 rounded-full bg-sky-400" aria-label="Unread" />}
                                                                    <span className="truncate font-medium text-foreground">{n.title}</span>
                                                                </span>
                                                                <span className="shrink-0 text-xs text-muted-foreground" title={new Date(n.created_time).toLocaleString()}>
                                                                    {formatNotificationRelativeTime(n.created_time)}
                                                                </span>
                                                            </span>
                                                            {n.message && (
                                                                <span className="mt-0.5 block text-sm text-muted-foreground line-clamp-2">
                                                                    {n.message}
                                                                </span>
                                                            )}
                                                        </span>
                                                    </button>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    )}

                    {!loading && filtered.length > 0 && (
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={setCurrentPage}
                            totalResult={filtered.length}
                            dataName="notifications"
                            pageSize={PAGE_SIZE}
                        />
                    )}
                </div>
            </Card>

            <FiltersDialog open={isFilterOpen} onClose={() => setIsFilterOpen(false)} onReset={resetFilters}>
                <div>
                    <Text>Category</Text>
                    <Select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)}>
                        <option value="">All ({notifications.length})</option>
                        {NOTIFICATION_CATEGORIES.map((cat) => (
                            <option key={cat.value} value={cat.value}>
                                {cat.label} ({categoryCounts[cat.value] || 0})
                            </option>
                        ))}
                    </Select>
                </div>
                <div>
                    <Text>Status</Text>
                    <Select value={unreadOnly ? 'unread' : 'all'} onChange={(e) => setUnreadOnly(e.target.value === 'unread')}>
                        <option value="all">All</option>
                        <option value="unread">Unread only</option>
                    </Select>
                </div>
            </FiltersDialog>

            <Dialog size="lg" open={!!selectedNotification} onClose={() => setSelectedNotification(null)}>
                {selectedNotification && (
                    <>
                        <DialogTitle>{selectedNotification.title}</DialogTitle>
                        <DialogBody className="space-y-4">
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                                <span className="text-sm text-zinc-500 dark:text-zinc-400">
                                    {new Date(selectedNotification.created_time).toLocaleString()}
                                </span>
                                <Badge color={selectedNotification.type === 'TRACKING_STALE' ? 'amber' : 'zinc'}>
                                    {selectedNotification.type === 'TRACKING_STALE' ? 'Tracking' : selectedNotification.type}
                                </Badge>
                            </div>
                            {selectedNotification.message && (
                                <p className="whitespace-pre-wrap break-words text-sm text-zinc-700 dark:text-zinc-300">
                                    {selectedNotification.message}
                                </p>
                            )}
                        </DialogBody>
                        <DialogActions>
                            <Button plain onClick={() => setSelectedNotification(null)}>Close</Button>
                            {selectedNotification.entity_type === 'tracking_order' && selectedNotification.link_url && (
                                <Button onClick={() => navigate(selectedNotification.link_url!)}>
                                    View tracking order
                                </Button>
                            )}
                        </DialogActions>
                    </>
                )}
            </Dialog>

            <Dialog size="md" open={clearDialogOpen} onClose={() => !clearing && setClearDialogOpen(false)}>
                <DialogTitle>Clear all notifications?</DialogTitle>
                <DialogDescription>
                    This removes all notifications from your list. They won't reappear unless something new happens
                    (e.g. a tracking order goes stale again).
                </DialogDescription>
                <DialogBody />
                <DialogActions>
                    <Button plain onClick={() => setClearDialogOpen(false)} disabled={clearing}>Cancel</Button>
                    <Button color="red" onClick={handleClearAll} disabled={clearing}>
                        {clearing ? 'Clearing...' : 'Clear all'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default NotificationsPage;
